/*
 * Accessible focus management for overlays / popups (WCAG 2.1 — 2.1.2 No
 * Keyboard Trap, 2.4.3 Focus Order, 4.1.2 Name/Role/Value).
 *
 * Popups in this app are server-rendered and either injected by HTMX into
 * #popup-placeholder (id="popup" / id="popup-dirty") or rendered directly
 * (class="nv-popup", e.g. the cookie overlay). Rather than wiring every view,
 * we observe the DOM for popups being added/removed and manage focus centrally:
 *
 *   on open  -> remember the trigger, mark the dialog as a modal, make the rest
 *               of the page inert, and move focus to the first control inside.
 *   while open -> Tab / Shift+Tab cycle within the topmost popup; Escape cancels
 *               it (clicks a close/cancel affordance, or dismisses the transient
 *               confirmation dialogs #popup / #popup-dirty outright).
 *   on close -> un-inert the page and return focus to the triggering element.
 */
(function () {
    'use strict';

    const POPUP_SELECTOR = '#popup, #popup-dirty, .nv-popup';
    const DIALOG_SELECTOR = '.nv-panel, .nv-popup__dialog';
    const CLOSE_AFFORDANCE_SELECTOR = '.nv-popup__close, [data-popup-close]';
    // Transient confirmation dialogs that Escape may dismiss outright (cancel).
    const CANCELLABLE_POPUP_IDS = ['popup', 'popup-dirty'];
    const FOCUSABLE_SELECTOR = [
        'a[href]', 'area[href]', 'button:not([disabled])',
        'input:not([disabled]):not([type="hidden"])', 'select:not([disabled])',
        'textarea:not([disabled])', 'iframe', 'audio[controls]', 'video[controls]',
        '[contenteditable]:not([contenteditable="false"])',
        '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    // Open popups, oldest first. Each entry remembers the element that had focus
    // before the popup opened, so it can be restored on close.
    const openPopups = [];
    // Body-level elements made inert while a popup is open; reverted when the
    // last popup closes.
    let inertedElements = [];

    const topPopup = () =>
        openPopups.length ? openPopups[openPopups.length - 1].popup : null;

    const isVisible = (element) => {
        const style = getComputedStyle(element);
        if (style.display === 'none' || style.visibility === 'hidden') {
            return false;
        }
        return Boolean(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
    };

    const focusableElementsWithin = (container) =>
        Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(isVisible);

    const dialogOf = (popup) => popup.querySelector(DIALOG_SELECTOR) || popup;

    const moveFocusInto = (popup) => {
        const dialog = dialogOf(popup);
        if (!focusableElementsWithin(popup).length && !dialog.hasAttribute('tabindex')) {
            dialog.setAttribute('tabindex', '-1');
        }
        requestAnimationFrame(() => {
            if (!document.contains(popup)) {
                return;
            }
            const autofocus = popup.querySelector('[autofocus]');
            const target = (autofocus && isVisible(autofocus))
                ? autofocus
                : (focusableElementsWithin(popup)[0] || dialog);
            target.focus();
        });
    };

    const makeBackgroundInert = (popup) => {
        let popupContainer = popup;
        while (popupContainer.parentElement && popupContainer.parentElement !== document.body) {
            popupContainer = popupContainer.parentElement;
        }
        if (inertedElements.length) {
            // Already inert for an earlier popup — just ensure this popup's
            // container wasn't inerned by that earlier call.
            const index = inertedElements.indexOf(popupContainer);
            if (index !== -1) {
                popupContainer.removeAttribute('inert');
                inertedElements.splice(index, 1);
            }
            return;
        }
        for (const child of document.body.children) {
            const isPopupContainer = child === popupContainer;
            const isScript = child.tagName === 'SCRIPT';
            if (!isPopupContainer && !isScript && !child.hasAttribute('inert')) {
                child.setAttribute('inert', '');
                inertedElements.push(child);
            }
        }
    };

    const clearBackgroundInert = () => {
        if (openPopups.length > 0) {
            return;
        }
        inertedElements.forEach((element) => element.removeAttribute('inert'));
        inertedElements = [];
    };

    const activatePopup = (popup) => {
        const alreadyTracked = openPopups.some((entry) => entry.popup === popup);
        if (alreadyTracked) {
            return;
        }
        openPopups.push({ popup, previouslyFocused: document.activeElement });

        const dialog = dialogOf(popup);
        if (!popup.getAttribute('role') && !dialog.getAttribute('role')) {
            dialog.setAttribute('role', 'dialog');
        }
        dialog.setAttribute('aria-modal', 'true');

        makeBackgroundInert(popup);
        moveFocusInto(popup);
    };

    const deactivatePopup = (closedEntry) => {
        const popupBeneath = topPopup();
        if (popupBeneath) {
            // A popup beneath remains open — keep focus trapped inside it.
            moveFocusInto(popupBeneath);
            return;
        }

        clearBackgroundInert();
        const trigger = closedEntry.previouslyFocused;
        if (trigger && typeof trigger.focus === 'function' && document.contains(trigger)) {
            trigger.focus();
        }
    };

    const handleTab = (event, popup) => {
        const focusables = focusableElementsWithin(popup);
        if (focusables.length === 0) {
            event.preventDefault();
            dialogOf(popup).focus();
            return;
        }

        const firstFocusable = focusables[0];
        const lastFocusable = focusables[focusables.length - 1];
        const active = document.activeElement;

        if (event.shiftKey) {
            if (active === firstFocusable || !popup.contains(active)) {
                event.preventDefault();
                lastFocusable.focus();
            }
        } else if (active === lastFocusable || !popup.contains(active)) {
            event.preventDefault();
            firstFocusable.focus();
        }
    };

    const handleEscape = (event, popup) => {
        // 2.1.2: keyboard users must be able to dismiss. Opt out with
        // data-no-escape (e.g. mandatory consent dialogs).
        if (popup.hasAttribute('data-no-escape')) {
            return;
        }

        // Prefer an explicit close/cancel control so its HTMX/JS side effects run.
        const closeAffordance = popup.querySelector(CLOSE_AFFORDANCE_SELECTOR);
        if (closeAffordance) {
            event.preventDefault();
            closeAffordance.click();
            return;
        }

        // The transient confirmation dialogs are always cancellable: removing the
        // element is equivalent to "cancel" since no confirm action is taken, and
        // the observer below restores focus and clears the background inert.
        // Standalone overlays (e.g. cookie consent) are left alone unless they
        // expose an explicit close affordance above.
        if (CANCELLABLE_POPUP_IDS.includes(popup.id)) {
            event.preventDefault();
            popup.remove();
        }
    };

    const onKeydown = (event) => {
        const popup = topPopup();
        if (!popup) {
            return;
        }
        if (event.key === 'Tab') {
            handleTab(event, popup);
        } else if (event.key === 'Escape') {
            handleEscape(event, popup);
        }
    };

    const popupsWithin = (node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) {
            return [];
        }
        const matches = node.matches && node.matches(POPUP_SELECTOR) ? [node] : [];
        const descendants = node.querySelectorAll ? Array.from(node.querySelectorAll(POPUP_SELECTOR)) : [];
        return matches.concat(descendants);
    };

    const onPopupAdded = (node) => {
        popupsWithin(node).forEach(activatePopup);
    };

    const onPopupRemoved = (node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) {
            return;
        }
        // Walk newest-first so splicing doesn't shift entries we still need to check.
        for (let index = openPopups.length - 1; index >= 0; index -= 1) {
            const { popup } = openPopups[index];
            const wasRemoved = popup === node || (node.nodeType === Node.ELEMENT_NODE && node.contains(popup));
            if (wasRemoved) {
                const [closedEntry] = openPopups.splice(index, 1);
                deactivatePopup(closedEntry);
            }
        }
    };

    const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            mutation.addedNodes.forEach(onPopupAdded);
            mutation.removedNodes.forEach(onPopupRemoved);
        }
    });

    const init = () => {
        document.addEventListener('keydown', onKeydown, true);
        observer.observe(document.body, { childList: true, subtree: true });
        // Pick up any popup already present at page load (e.g. cookie overlay).
        document.querySelectorAll(POPUP_SELECTOR).forEach(activatePopup);
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
